/* =========================================================
   ALOR Newsletter Popup
   - Triggers at 50% scroll depth
   - Once per visitor (30-day cookie)
   - Two-step flow with Klaviyo Client API submission
   ========================================================= */

(function () {
  'use strict';

  var COOKIE_NAME = 'alor_popup_seen';
  var COOKIE_DAYS = 30;
  var SCROLL_TRIGGER_PERCENT = 50;
  var KLAVIYO_API_BASE = 'https://a.klaviyo.com/client/subscriptions/';
  var KLAVIYO_API_REVISION = '2024-10-15';

  // --- Run on DOMContentLoaded (script is deferred, but be defensive) ---
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    var popup = document.getElementById('alor-popup');
    if (!popup) return;

    // Don't show if already seen
    if (getCookie(COOKIE_NAME)) return;

    var listId = popup.getAttribute('data-list-id');
    var publicKey = popup.getAttribute('data-public-key');

    if (!listId || !publicKey) {
      console.warn('[ALOR Popup] Missing list ID or public key');
      return;
    }

    // --- Scroll trigger ---
    var triggered = false;
    var onScroll = function () {
      if (triggered) return;
      var docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      var scrolled = (window.scrollY / docHeight) * 100;
      if (scrolled >= SCROLL_TRIGGER_PERCENT) {
        triggered = true;
        window.removeEventListener('scroll', onScroll);
        openPopup(popup);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    // --- Close handlers ---
    var closers = popup.querySelectorAll('[data-alor-close]');
    closers.forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        closePopup(popup);
      });
    });

    // ESC to close
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && popup.getAttribute('aria-hidden') === 'false') {
        closePopup(popup);
      }
    });

    // --- Step 1 → Step 2 ---
    var advanceBtn = popup.querySelector('[data-alor-advance]');
    if (advanceBtn) {
      advanceBtn.addEventListener('click', function () {
        showStep(popup, 2);
        // Focus the email input for accessibility
        var emailInput = popup.querySelector('#alor-popup-email');
        if (emailInput) {
          setTimeout(function () { emailInput.focus(); }, 100);
        }
      });
    }

    // --- Form submission ---
    var form = popup.querySelector('[data-alor-form]');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        handleSubmit(popup, listId, publicKey);
      });
    }
  }

  // --- Open / close ---

  function openPopup(popup) {
    popup.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closePopup(popup) {
    popup.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    // Set cookie so we don't show again
    setCookie(COOKIE_NAME, '1', COOKIE_DAYS);
  }

  // --- Step navigation ---

  function showStep(popup, stepNumber) {
    var steps = popup.querySelectorAll('[data-alor-step]');
    steps.forEach(function (step) {
      if (parseInt(step.getAttribute('data-alor-step'), 10) === stepNumber) {
        step.classList.add('alor-popup__step--active');
      } else {
        step.classList.remove('alor-popup__step--active');
      }
    });
  }

  // --- Form submission ---

  function handleSubmit(popup, listId, publicKey) {
    var input = popup.querySelector('#alor-popup-email');
    var errorEl = popup.querySelector('[data-alor-error]');
    var submitBtn = popup.querySelector('[data-alor-form] button[type="submit"]');

    if (!input || !submitBtn) return;

    var email = (input.value || '').trim();

    // Basic validation
    if (!email || !isValidEmail(email)) {
      showError(errorEl, 'Please enter a valid email.');
      return;
    }

    showError(errorEl, '');
    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending…';

    subscribeToKlaviyo(email, listId, publicKey)
      .then(function () {
        // Set cookie so popup won't show again
        setCookie(COOKIE_NAME, '1', COOKIE_DAYS);
        showStep(popup, 3);
        // Auto-close after a beat
        setTimeout(function () { closePopup(popup); }, 4000);
      })
      .catch(function (err) {
        console.error('[ALOR Popup] Subscribe failed:', err);
        submitBtn.disabled = false;
        submitBtn.textContent = 'Confirm';
        showError(errorEl, 'Something went wrong. Please try again.');
      });
  }

  function showError(el, message) {
    if (!el) return;
    el.textContent = message;
  }

  function isValidEmail(email) {
    // Pragmatic check — Klaviyo will do the real validation
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  // --- Klaviyo Client API ---

  function subscribeToKlaviyo(email, listId, publicKey) {
    var url = KLAVIYO_API_BASE + '?company_id=' + encodeURIComponent(publicKey);

    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Homepage Popup',
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email }
            }
          }
        },
        relationships: {
          list: {
            data: { type: 'list', id: listId }
          }
        }
      }
    };

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': KLAVIYO_API_REVISION
      },
      body: JSON.stringify(payload)
    }).then(function (response) {
      // Klaviyo returns 202 Accepted on success
      if (response.status === 202 || response.ok) {
        return true;
      }
      return response.text().then(function (text) {
        throw new Error('Klaviyo error ' + response.status + ': ' + text);
      });
    });
  }

  // --- Cookie helpers ---

  function setCookie(name, value, days) {
    var expires = '';
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + value + expires + '; path=/; SameSite=Lax';
  }

  function getCookie(name) {
    var nameEQ = name + '=';
    var parts = document.cookie.split(';');
    for (var i = 0; i < parts.length; i++) {
      var c = parts[i].trim();
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length);
    }
    return null;
  }
})();